// /email-log — Email Activity: every quote, PO, staff notification and
// template email the system has sent, with delivery status + one-click retry.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, Loader2, RefreshCw, Mail, AlertTriangle,
  FileText, Truck, Bell, Search, RotateCcw, Zap,
} from "lucide-react";

const STATUS_STYLES = {
  sent:      { icon: CheckCircle2,  bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  scheduled: { icon: Clock,         bg: "bg-cyan-50",    border: "border-cyan-200",    text: "text-cyan-700" },
  failed:    { icon: XCircle,       bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800" },
  cancelled: { icon: XCircle,       bg: "bg-slate-100",  border: "border-slate-300",   text: "text-slate-600" },
  mocked:    { icon: AlertTriangle, bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800" },
};

const KIND_META = {
  quote:              { label: "Quote",         icon: FileText, cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  purchase_order:     { label: "Purchase order", icon: Truck,   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  staff_notification: { label: "Staff notify",  icon: Bell,     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  custom_automation:  { label: "Automation",    icon: Zap,      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  template:           { label: "Template",      icon: Mail,     cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.mocked;
  const Icon = s.icon;
  return (
    <Badge className={`rounded-sm font-normal border ${s.bg} ${s.text} ${s.border}`}>
      <Icon size={11} className="mr-1" /> {status}
    </Badge>
  );
};

const KindBadge = ({ kind }) => {
  const k = KIND_META[kind] || { label: kind || "Email", icon: Mail, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  const Icon = k.icon;
  return (
    <Badge className={`rounded-sm font-normal border whitespace-nowrap ${k.cls}`}>
      <Icon size={11} className="mr-1" /> {k.label}
    </Badge>
  );
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function EmailLogPage() {
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [retrying, setRetrying] = useState(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["email-activity", kind, status],
    queryFn: async () => {
      const params = {};
      if (kind !== "all") params.kind = kind;
      if (status !== "all") params.status = status;
      return (await api.get("/email-activity", { params })).data;
    },
    refetchInterval: 30000,
  });

  const items = (data?.items || []).filter((r) => {
    if (!q.trim()) return true;
    const ql = q.trim().toLowerCase();
    return (r.to || "").toLowerCase().includes(ql) || (r.subject || "").toLowerCase().includes(ql);
  });
  const counts = data?.counts || {};

  const retry = async (row) => {
    setRetrying(row.id);
    try {
      const res = (await api.post(`/email-activity/${row.id}/retry`)).data;
      if (res.status === "sent") toast.success(`Re-sent to ${res.to}`);
      else toast.error(`Retry failed: ${res.error || res.status}`);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not retry this email");
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="email-activity-page">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[2px] text-cyan-600">Communications</div>
          <h1 className="font-display font-bold text-slate-900 text-3xl leading-tight mt-1">Email activity</h1>
          <p className="text-xs text-slate-500 mt-1">
            Every quote, purchase order and staff notification the system has emailed, with its delivery status.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            {counts.sent > 0 && <span className="text-emerald-700" data-testid="count-sent">{counts.sent} sent</span>}
            {counts.failed > 0 && <span className="text-rose-700" data-testid="count-failed">{counts.failed} failed</span>}
            {counts.scheduled > 0 && <span className="text-cyan-700">{counts.scheduled} scheduled</span>}
          </div>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}
            className="rounded-sm h-9" data-testid="email-activity-refresh-btn">
            {isFetching ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />}
            Refresh
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject…"
            className="rounded-sm h-9 pl-8 w-64" data-testid="email-activity-search" />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="rounded-sm h-9 w-44" data-testid="email-activity-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="quote">Quotes</SelectItem>
            <SelectItem value="purchase_order">Purchase orders</SelectItem>
            <SelectItem value="staff_notification">Staff notifications</SelectItem>
            <SelectItem value="template">Templates</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-sm h-9 w-36" data-testid="email-activity-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="mocked">Mocked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <Card className="rounded-sm p-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></Card>}

      {!isLoading && (
        <Card className="rounded-sm p-0 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm" data-testid="email-activity-empty">No emails match these filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="email-activity-table">
                <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Type</th>
                    <th className="text-left px-4 py-2">To</th>
                    <th className="text-left px-4 py-2">Subject</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">By</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={`${r.source}-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`email-row-${r.id}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap text-slate-700">{fmtDate(r.when)}</td>
                      <td className="px-4 py-2.5"><KindBadge kind={r.kind} /></td>
                      <td className="px-4 py-2.5 max-w-[200px]">
                        <div className="text-slate-900 font-medium truncate" title={r.to}>{r.to}</div>
                        {(r.cc || []).length > 0 && (
                          <div className="text-[10px] text-cyan-700 truncate" title={r.cc.join(", ")} data-testid={`email-cc-${r.id}`}>
                            CC: {r.cc.join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 truncate max-w-[260px]" title={r.subject}>
                        {r.subject}
                        {r.retry_of && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-slate-400">retry</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={r.status} />
                        {r.error && <div className="text-rose-700 text-[10px] mt-1 max-w-[220px] truncate" title={r.error}>{r.error}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap truncate max-w-[130px]">{r.sent_by || "—"}</td>
                      <td className="px-4 py-2.5 text-right">
                        {r.status === "failed" && (
                          <Button size="sm" variant="outline" onClick={() => retry(r)}
                            disabled={retrying === r.id}
                            className="rounded-sm h-7 text-[11px] border-rose-200 text-rose-700 hover:bg-rose-50"
                            data-testid={`retry-email-${r.id}`}>
                            {retrying === r.id
                              ? <Loader2 size={11} className="animate-spin mr-1" />
                              : <RotateCcw size={11} className="mr-1" />}
                            Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
